import { all, call, fork, put, select, takeEvery } from "redux-saga/effects";
import { types } from "./types";
import {
  GET_TEAMS,
  CREATE_TEAM,
  UPDATE_TEAM,
  ARCHIVE_TEAM,
  SET_USER_TEAM,
} from "../../api/team";
import { getCompanyUsers } from "../company/actions";

const safeCall = (meta, key, ...args) => {
  if (meta && typeof meta[key] === "function") {
    try {
      meta[key](...args);
    } catch {
      /* swallow — callbacks shouldn't break the saga */
    }
  }
};

function* fetchTeams(action) {
  try {
    const { data } = yield call(GET_TEAMS);
    const list = Array.isArray(data?.teams) ? data.teams : [];
    const counts = data?.counts && typeof data.counts === "object" ? data.counts : {};
    yield put({ type: types.GET_TEAMS_SUCCESS, list, counts });
    safeCall(action.meta, "onSuccess", { list, counts });
  } catch (err) {
    const error = err?.response?.data?.message || err?.message || "Failed to load teams";
    yield put({ type: types.GET_TEAMS_FAILED, error });
    safeCall(action.meta, "onError", error);
  }
}

function* createTeam(action) {
  try {
    const { data: team } = yield call(CREATE_TEAM, action.payload);
    yield put({ type: types.CREATE_TEAM_SUCCESS, team });
    safeCall(action.meta, "onSuccess", team);
  } catch (err) {
    const error = err?.response?.data?.message || err?.message || "Failed to create team";
    yield put({ type: types.CREATE_TEAM_FAILED, error });
    safeCall(action.meta, "onError", error);
  }
}

function* updateTeam(action) {
  const { id, name, color } = action.payload;
  // Snapshot the old name BEFORE the request so we can migrate counts/users
  // on success regardless of what shape the server returns.
  const oldName = yield select((s) => {
    const t = (s.teamsReducer?.list || []).find((x) => x._id === id);
    return t?.name || "";
  });
  try {
    const body = {};
    if (typeof name === "string") body.name = name;
    if (typeof color === "string") body.color = color;
    const { data: team } = yield call(UPDATE_TEAM, id, body);
    yield put({ type: types.UPDATE_TEAM_SUCCESS, team, oldName });
    // If the name changed, every user that had the old name now has the new
    // one on the server. Refresh users so user.team flows through Redux.
    if (oldName && team?.name && oldName !== team.name) {
      yield put(getCompanyUsers());
    }
    safeCall(action.meta, "onSuccess", team);
  } catch (err) {
    const error = err?.response?.data?.message || err?.message || "Failed to update team";
    yield put({ type: types.UPDATE_TEAM_FAILED, error });
    safeCall(action.meta, "onError", error);
  }
}

function* archiveTeam(action) {
  const { id } = action.payload;
  try {
    yield call(ARCHIVE_TEAM, id);
    yield put({ type: types.ARCHIVE_TEAM_SUCCESS, id });
    // Members were unassigned server-side — refresh users so their team
    // field clears in the table.
    yield put(getCompanyUsers());
    safeCall(action.meta, "onSuccess", { id });
  } catch (err) {
    const error = err?.response?.data?.message || err?.message || "Failed to archive team";
    yield put({ type: types.ARCHIVE_TEAM_FAILED, error });
    safeCall(action.meta, "onError", error);
  }
}

function* assignUserTeam(action) {
  const { userId, team } = action.payload;
  // Snapshot previous team for optimistic count update + rollback on failure.
  const previousTeam = yield select((s) => {
    const dict = s.companyReducer?.usersDictionary || {};
    return dict[userId]?.team || "";
  });
  try {
    yield call(SET_USER_TEAM, userId, team || "");
    yield put({
      type: types.ASSIGN_USER_TEAM_SUCCESS,
      userId,
      team: team || "",
      previousTeam,
    });
    // Refresh users so the dictionary reflects the new team value.
    yield put(getCompanyUsers());
    safeCall(action.meta, "onSuccess", { userId, team });
  } catch (err) {
    const error = err?.response?.data?.message || err?.message || "Failed to set team";
    yield put({ type: types.ASSIGN_USER_TEAM_FAILED, error });
    safeCall(action.meta, "onError", error);
  }
}

function* watchTeams() {
  yield takeEvery(types.GET_TEAMS_REQUEST, fetchTeams);
  yield takeEvery(types.CREATE_TEAM_REQUEST, createTeam);
  yield takeEvery(types.UPDATE_TEAM_REQUEST, updateTeam);
  yield takeEvery(types.ARCHIVE_TEAM_REQUEST, archiveTeam);
  yield takeEvery(types.ASSIGN_USER_TEAM_REQUEST, assignUserTeam);
}

function* rootSaga() {
  yield all([fork(watchTeams)]);
}

export default rootSaga;
